import React, { useState } from "react";
import { Star, CheckCircle, ArrowRight, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitFeedback } from "@/lib/api";

const SERVICE_TYPES = [
  "Routine Janitorial Service","Medical Facility Cleaning","Floor Care","Carpet Cleaning",
  "Window Cleaning","Deep Cleaning","One-Time Service","Other",
];
const DID_WELL = [
  "Quality of Cleaning","Professional Staff","Reliable Service","Attention to Detail",
  "Strong Communication","Fast Response","Friendly Team","Exceeded Expectations","Other",
];
const RECOMMEND = ["Definitely","Probably","Maybe","Probably Not","No"];
const SHARE = ["Yes, with my name and company","Yes, anonymously","No"];
const YN = ["Yes","No"];

const RATING_ROWS = [
  { key: "cleaning", label: "Quality of Cleaning" },
  { key: "professionalism", label: "Professionalism" },
  { key: "reliability", label: "Reliability" },
  { key: "communication", label: "Communication" },
  { key: "attention_to_detail", label: "Attention to Detail" },
];

// Public Google review link — replace with the actual Empire Serv Google Maps CID/place-id when available
const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=";

const INITIAL = {
  facility_name: "", facility_city: "", date_of_service: "", service_type: "",
  overall_rating: 0,
  ratings: { cleaning: 0, professionalism: 0, reliability: 0, communication: 0, attention_to_detail: 0 },
  did_well: [],
  experience_notes: "", improvement_notes: "",
  would_recommend: "",
  contact_management: "",
  contact_name: "", contact_phone: "", contact_email: "",
  share_permission: "",
};

export default function FeedbackForm() {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRating = (k, v) => setForm((f) => ({ ...f, ratings: { ...f.ratings, [k]: v } }));
  const toggleDidWell = (v) =>
    setForm((f) => ({ ...f, did_well: f.did_well.includes(v) ? f.did_well.filter((x) => x !== v) : [...f.did_well, v] }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.facility_name || !form.facility_city) return toast.error("Please enter facility name and city.");
    if (!form.overall_rating) return toast.error("Please provide an overall rating.");
    setBusy(true);
    try {
      await submitFeedback(form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Thank you — your feedback has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit feedback. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const highRating = form.overall_rating >= 4;
    return (
      <div data-testid="feedback-success-message" className="bg-white border border-[var(--es-border)] rounded-sm p-10 md:p-14 text-center">
        <CheckCircle size={64} weight="fill" className="mx-auto text-[var(--es-accent)]" />
        {highRating ? (
          <>
            <h3 className="mt-6 text-3xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Thank you for your feedback!</h3>
            <p className="mt-4 text-[var(--es-text-muted)] max-w-xl mx-auto">
              We appreciate the opportunity to serve your organization and are pleased to hear about your experience.
            </p>
            <p className="mt-4 text-[var(--es-navy)] font-semibold">Would you be willing to share your experience on Google?</p>
            <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="feedback-google-review-link" className="es-btn-accent mt-6 inline-flex">
              Leave a Google Review <ArrowSquareOut size={18} />
            </a>
          </>
        ) : (
          <>
            <h3 className="mt-6 text-3xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Thank you for your honest feedback.</h3>
            <p className="mt-4 text-[var(--es-text-muted)] max-w-xl mx-auto">
              We are sorry that we did not fully meet your expectations. Your comments have been forwarded to our management team for review. If you requested follow-up, a member of our team will contact you promptly.
            </p>
            <p className="mt-4 text-[var(--es-text-muted)] max-w-xl mx-auto italic">
              Your feedback gives us the opportunity to improve and make things right.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <form data-testid="feedback-form" onSubmit={onSubmit} className="bg-white border border-[var(--es-border)] rounded-sm p-6 md:p-10 space-y-10">
      <Section title="Service Information" number="1">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Facility Name *"><input required className="es-input" value={form.facility_name} onChange={(e) => set("facility_name", e.target.value)} data-testid="feedback-input-facility-name" /></Field>
          <Field label="Facility City *"><input required className="es-input" value={form.facility_city} onChange={(e) => set("facility_city", e.target.value)} data-testid="feedback-input-city" /></Field>
          <Field label="Date of Service"><input type="date" className="es-input" value={form.date_of_service} onChange={(e) => set("date_of_service", e.target.value)} data-testid="feedback-input-date" /></Field>
          <Field label="Service Type">
            <select className="es-input" value={form.service_type} onChange={(e) => set("service_type", e.target.value)} data-testid="feedback-input-service-type">
              <option value="">Select…</option>
              {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Overall Experience" number="2">
        <div className="es-label">How would you rate your overall experience? *</div>
        <StarRow value={form.overall_rating} onChange={(v) => set("overall_rating", v)} size={40} testPrefix="feedback-overall" />
      </Section>

      <Section title="Service Ratings" number="3">
        <div className="grid gap-4">
          {RATING_ROWS.map((r) => (
            <div key={r.key} className="grid grid-cols-1 md:grid-cols-2 items-center gap-2 py-2 border-b border-[var(--es-border)] last:border-0">
              <div className="text-sm font-semibold text-[var(--es-navy)]">{r.label}</div>
              <StarRow value={form.ratings[r.key]} onChange={(v) => setRating(r.key, v)} testPrefix={`feedback-rate-${r.key}`} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Your Feedback" number="4">
        <div className="es-label">What did we do well?</div>
        <CheckGrid items={DID_WELL} selected={form.did_well} onToggle={toggleDidWell} testPrefix="feedback-did-well" cols="grid-cols-2 md:grid-cols-3" />

        <div className="mt-5 grid gap-5">
          <Field label="Tell us more about your experience.">
            <textarea rows={4} className="es-input" value={form.experience_notes} onChange={(e) => set("experience_notes", e.target.value)} data-testid="feedback-input-experience" />
          </Field>
          <Field label="Where can we improve?">
            <textarea rows={3} className="es-input" value={form.improvement_notes} onChange={(e) => set("improvement_notes", e.target.value)} data-testid="feedback-input-improve" />
          </Field>
        </div>

        <div className="mt-5">
          <div className="es-label">Would you recommend Empire Serv to another business?</div>
          <RadioGrid items={RECOMMEND} selected={form.would_recommend} onSelect={(v) => set("would_recommend", v)} testPrefix="feedback-recommend" cols="grid-cols-1 md:grid-cols-5" />
        </div>
      </Section>

      <Section title="Follow-Up Information" number="5">
        <div className="es-label">Would you like a member of management to contact you?</div>
        <RadioGrid items={YN} selected={form.contact_management} onSelect={(v) => set("contact_management", v)} testPrefix="feedback-contact" cols="grid-cols-2" />

        {form.contact_management === "Yes" && (
          <div className="mt-5 grid md:grid-cols-3 gap-5">
            <Field label="Name"><input className="es-input" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="feedback-input-contact-name" /></Field>
            <Field label="Phone Number"><input type="tel" className="es-input" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="feedback-input-contact-phone" /></Field>
            <Field label="Email Address"><input type="email" className="es-input" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="feedback-input-contact-email" /></Field>
          </div>
        )}

        <div className="mt-6">
          <div className="es-label">May we share your comments on our website or marketing materials?</div>
          <RadioGrid items={SHARE} selected={form.share_permission} onSelect={(v) => set("share_permission", v)} testPrefix="feedback-share" cols="grid-cols-1 md:grid-cols-3" />
        </div>
      </Section>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[var(--es-border)]">
        <p className="text-xs text-[var(--es-text-muted)] max-w-md">
          Your feedback helps us recognize outstanding team members and continue delivering exceptional service.
        </p>
        <button type="submit" disabled={busy} data-testid="feedback-submit-button" className="es-btn-accent whitespace-nowrap disabled:opacity-50">
          {busy ? "Submitting…" : "Share My Feedback"} <ArrowRight size={18} />
        </button>
      </div>
    </form>
  );
}

function Section({ title, number, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 bg-[var(--es-navy)] text-[var(--es-accent)] rounded-sm flex items-center justify-center font-bold text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{number}</span>
        <h3 className="text-xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="es-label">{label}</span>
      {children}
    </label>
  );
}

function StarRow({ value, onChange, size = 28, testPrefix }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="flex items-center gap-1" data-testid={testPrefix}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          data-testid={`${testPrefix}-${n}`}
          className="transition-transform hover:scale-110"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star size={size} weight={active >= n ? "fill" : "regular"} className={active >= n ? "text-[var(--es-accent)]" : "text-[var(--es-border)]"} />
        </button>
      ))}
      {value > 0 && <span className="ml-2 text-xs font-semibold text-[var(--es-text-muted)]">{value}/5</span>}
    </div>
  );
}

function CheckGrid({ items, selected, onToggle, testPrefix, cols }) {
  return (
    <div className={`grid ${cols} gap-2 mt-2`}>
      {items.map((v) => {
        const on = selected.includes(v);
        const id = `${testPrefix}-${v.replace(/\W+/g, "-").toLowerCase()}`;
        return (
          <button type="button" key={v} data-testid={id} onClick={() => onToggle(v)}
            className={`text-xs md:text-sm text-left border px-3 py-2.5 rounded-sm transition-colors flex items-center gap-2 ${on ? "border-[var(--es-navy)] bg-[var(--es-navy)] text-white" : "border-[var(--es-border)] bg-white text-[var(--es-text)] hover:border-[var(--es-navy)]"}`}>
            <span className={`w-3.5 h-3.5 border rounded-[2px] flex items-center justify-center flex-shrink-0 ${on ? "bg-[var(--es-accent)] border-[var(--es-accent)]" : "border-[var(--es-border)]"}`}>
              {on && <CheckCircle size={10} weight="fill" className="text-[var(--es-navy)]" />}
            </span>
            {v}
          </button>
        );
      })}
    </div>
  );
}

function RadioGrid({ items, selected, onSelect, testPrefix, cols }) {
  return (
    <div className={`grid ${cols} gap-2 mt-2`}>
      {items.map((v) => {
        const on = selected === v;
        const id = `${testPrefix}-${v.replace(/\W+/g, "-").toLowerCase()}`;
        return (
          <button type="button" key={v} data-testid={id} onClick={() => onSelect(v)}
            className={`text-xs md:text-sm text-left border px-3 py-2.5 rounded-sm transition-colors flex items-center gap-2 ${on ? "border-[var(--es-navy)] bg-[var(--es-navy)] text-white" : "border-[var(--es-border)] bg-white text-[var(--es-text)] hover:border-[var(--es-navy)]"}`}>
            <span className={`w-3.5 h-3.5 border rounded-full flex items-center justify-center flex-shrink-0 ${on ? "border-[var(--es-accent)]" : "border-[var(--es-border)]"}`}>
              {on && <span className="w-1.5 h-1.5 bg-[var(--es-accent)] rounded-full" />}
            </span>
            {v}
          </button>
        );
      })}
    </div>
  );
}
